/**
 * FORMA de um elemento do mapa — setor, subsetor ou posto.
 *
 * Até a v2 o mapa só tinha retângulos com posição automática. Agora o gestor
 * desenha o contorno que quiser (a fábrica da cliente-piloto não é feita de caixas),
 * então cada elemento guarda uma forma:
 *
 *     {tipo, x, y, largura, altura, pontos}
 *
 * - `tipo`: "retangulo" | "elipse" | "poligono".
 * - `x`, `y`, `largura`, `altura`: a CAIXA ENVOLVENTE, sempre — é o que o
 *   posicionamento (planta.js) usa para saber onde cabe bancada e peça, sem
 *   precisar entender o contorno.
 * - `pontos`: `[{x, y}]` em coordenadas do MUNDO (as mesmas do mapa), só no
 *   polígono; retângulo e elipse trazem lista vazia. Absolutas e não
 *   relativas à caixa de propósito: ponto relativo obriga a recalcular tudo a
 *   cada arraste e foi de onde vinham os erros de meio pixel na v1 do
 *   desenho. A caixa é DERIVADA dos pontos (`normalizar` a recalcula).
 *
 * O polígono NÃO repete o primeiro ponto no fim: quem desenha fecha com `Z`
 * (`paraPath`).
 *
 * Módulo puro: nada de DOM, nada de rede. É a base do desenho a mão livre
 * (`tracado.js`) e do posicionamento hierárquico (setor > subsetor > posto).
 */

export const TIPO_FORMA = Object.freeze({
  RETANGULO: "retangulo",
  ELIPSE: "elipse",
  POLIGONO: "poligono",
});

export const TIPOS_FORMA = Object.freeze(Object.values(TIPO_FORMA));

/**
 * Os mesmos limites que o backend valida (`mapa_producao/formas.py`): manter
 * a conta aqui evita mandar um desenho que volta 400 depois de o gestor ter
 * arrastado a sala inteira.
 */
export const LIMITES_FORMA = Object.freeze({
  COORDENADA_MINIMA: 0,
  COORDENADA_MAXIMA: 10000,
  LADO_MINIMO: 20,
  PONTOS_MINIMO: 3,
  PONTOS_MAXIMO: 64,
  CASAS_DECIMAIS: 2,
});

// Coordenadas são arredondadas a duas casas; uma tolerância uma ordem de
// grandeza menor deixa "encostado na borda" contar como dentro.
const TOLERANCIA = 0.001;
// Amostras do contorno de uma elipse-filha ao testar `contem`.
const AMOSTRAS_ELIPSE = 8;

const SEM_PONTOS = Object.freeze([]);

const finito = (valor) => typeof valor === "number" && Number.isFinite(valor);

function arredondar(valor) {
  const fator = 10 ** LIMITES_FORMA.CASAS_DECIMAIS;
  return Math.round(valor * fator) / fator;
}

function limitar(valor, minimo, maximo) {
  return Math.min(maximo, Math.max(minimo, valor));
}

const ponto = (x, y) => Object.freeze({ x: arredondar(x), y: arredondar(y) });

function pontosValidos(bruto) {
  if (!Array.isArray(bruto)) return [];
  return bruto
    .map((p) => (Array.isArray(p) ? { x: p[0], y: p[1] } : p))
    .filter((p) => p && finito(Number(p.x)) && finito(Number(p.y)))
    .map((p) => ({ x: Number(p.x), y: Number(p.y) }));
}

// Dedo tremido gera dezenas de pontos no mesmo lugar; ponto repetido só
// engorda o JSON e estraga o cálculo de ângulo em `tracado.js`.
function semConsecutivosIguais(pontos) {
  return pontos.filter((p, i) => {
    const anterior = pontos[i - 1];
    return !anterior || Math.abs(p.x - anterior.x) > TOLERANCIA || Math.abs(p.y - anterior.y) > TOLERANCIA;
  });
}

/**
 * Pontos crus (do dedo, do mouse ou de JSON) como `[{x, y}]` de números, sem
 * repetição consecutiva. Aceita `{x, y}` e o par `[x, y]`. É por aqui que
 * `tracado.js` entra: ele precisa do ponto de fechamento preservado.
 */
export function limparPontos(bruto) {
  return semConsecutivosIguais(pontosValidos(bruto));
}

function semRepetidos(pontos) {
  const limpos = semConsecutivosIguais(pontos);
  const primeiro = limpos[0];
  const ultimo = limpos[limpos.length - 1];
  if (
    limpos.length > 1 &&
    Math.abs(primeiro.x - ultimo.x) <= TOLERANCIA &&
    Math.abs(primeiro.y - ultimo.y) <= TOLERANCIA
  ) {
    return limpos.slice(0, -1);
  }
  return limpos;
}

// Mais pontos do que o backend aceita: mantém as pontas e rala o meio por
// igual. `tracado.js` já simplifica antes; isto é a rede de segurança.
function limitarQuantidade(pontos, maximo) {
  if (pontos.length <= maximo) return pontos;
  const passo = (pontos.length - 1) / (maximo - 1);
  const escolhidos = [];
  for (let i = 0; i < maximo; i += 1) escolhidos.push(pontos[Math.round(i * passo)]);
  return semRepetidos(escolhidos);
}

/** `{x, y, largura, altura}` que envolve uma lista de pontos (`null` se vazia). */
export function caixaDePontos(bruto) {
  const pontos = pontosValidos(bruto);
  if (pontos.length === 0) return null;
  const xs = pontos.map((p) => p.x);
  const ys = pontos.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, largura: Math.max(...xs) - x, altura: Math.max(...ys) - y };
}

// ------------------------------------------------------------ construção

/** Forma de caixa: `{x, y, largura, altura}` → retângulo normalizado. */
export function criarRetangulo(caixa) {
  return normalizar({ tipo: TIPO_FORMA.RETANGULO, ...caixa });
}

/** Elipse inscrita na caixa `{x, y, largura, altura}`. */
export function criarElipse(caixa) {
  return normalizar({ tipo: TIPO_FORMA.ELIPSE, ...caixa });
}

/** Polígono a partir dos pontos (a caixa envolvente é derivada). */
export function criarPoligono(pontos) {
  return normalizar({ tipo: TIPO_FORMA.POLIGONO, pontos });
}

// ------------------------------------------------------------- medidas

/**
 * `{x, y, largura, altura}` da forma. Aceita também uma caixa crua (os
 * elementos do layout da v2), para a transição não precisar de dois caminhos.
 */
export function caixaEnvolvente(forma) {
  if (!forma) return null;
  const pontos = pontosValidos(forma.pontos);
  if (forma.tipo === TIPO_FORMA.POLIGONO && pontos.length >= 2) return caixaDePontos(pontos);
  if (!finito(forma.x) || !finito(forma.y) || !finito(forma.largura) || !finito(forma.altura)) return null;
  return { x: forma.x, y: forma.y, largura: forma.largura, altura: forma.altura };
}

/** Caixa que envolve várias formas (`null` quando nenhuma é legível). */
export function caixaDeVarias(formas) {
  const caixas = (formas || []).map(caixaEnvolvente).filter(Boolean);
  if (caixas.length === 0) return null;
  const x = Math.min(...caixas.map((c) => c.x));
  const y = Math.min(...caixas.map((c) => c.y));
  const direita = Math.max(...caixas.map((c) => c.x + c.largura));
  const abaixo = Math.max(...caixas.map((c) => c.y + c.altura));
  return { x, y, largura: direita - x, altura: abaixo - y };
}

/** Centro da caixa envolvente (é onde o rótulo do setor é ancorado). */
export function centro(forma) {
  const caixa = caixaEnvolvente(forma);
  if (!caixa) return null;
  return { x: caixa.x + caixa.largura / 2, y: caixa.y + caixa.altura / 2 };
}

function areaDoPoligono(pontos) {
  let dobro = 0;
  for (let i = 0, j = pontos.length - 1; i < pontos.length; j = i, i += 1) {
    dobro += pontos[j].x * pontos[i].y - pontos[i].x * pontos[j].y;
  }
  return Math.abs(dobro) / 2;
}

/**
 * Área ocupada, na unidade do mundo ao quadrado. Serve para ordenar salas
 * (a maior desenha primeiro) e para a tela avisar que um posto ficou maior
 * que o subsetor que o contém.
 */
export function area(forma) {
  const caixa = caixaEnvolvente(forma);
  if (!caixa) return 0;
  if (forma.tipo === TIPO_FORMA.POLIGONO) {
    const pontos = pontosValidos(forma.pontos);
    return pontos.length >= LIMITES_FORMA.PONTOS_MINIMO ? areaDoPoligono(pontos) : 0;
  }
  if (forma.tipo === TIPO_FORMA.ELIPSE) return (Math.PI / 4) * caixa.largura * caixa.altura;
  return caixa.largura * caixa.altura;
}

// --------------------------------------------------------- normalização

function crescerParaMinimo(caixa, minimo) {
  const largura = Math.max(caixa.largura, minimo);
  const altura = Math.max(caixa.altura, minimo);
  return {
    x: caixa.x - (largura - caixa.largura) / 2,
    y: caixa.y - (altura - caixa.altura) / 2,
    largura,
    altura,
  };
}

// Puxa a caixa para dentro do mundo; se ela é maior que o mundo, encolhe.
function encaixarNoMundo(caixa) {
  const { COORDENADA_MINIMA: minimo, COORDENADA_MAXIMA: maximo } = LIMITES_FORMA;
  const largura = Math.min(caixa.largura, maximo - minimo);
  const altura = Math.min(caixa.altura, maximo - minimo);
  return {
    x: limitar(caixa.x, minimo, maximo - largura),
    y: limitar(caixa.y, minimo, maximo - altura),
    largura,
    altura,
  };
}

// Leva os pontos da caixa `de` para a caixa `para` (a mesma proporção).
function reprojetar(pontos, de, para) {
  const fx = de.largura > TOLERANCIA ? para.largura / de.largura : 0;
  const fy = de.altura > TOLERANCIA ? para.altura / de.altura : 0;
  return pontos.map((p) => ({
    x: para.x + (fx ? (p.x - de.x) * fx : para.largura / 2),
    y: para.y + (fy ? (p.y - de.y) * fy : para.altura / 2),
  }));
}

function normalizarCaixa(tipo, bruto) {
  const caixa = caixaEnvolvente(bruto);
  if (!caixa) return null;
  const ajustada = encaixarNoMundo(crescerParaMinimo(caixa, LIMITES_FORMA.LADO_MINIMO));
  return Object.freeze({
    tipo,
    x: arredondar(ajustada.x),
    y: arredondar(ajustada.y),
    largura: arredondar(ajustada.largura),
    altura: arredondar(ajustada.altura),
    pontos: SEM_PONTOS,
  });
}

function normalizarPoligono(bruto) {
  const pontos = limitarQuantidade(semRepetidos(pontosValidos(bruto.pontos)), LIMITES_FORMA.PONTOS_MAXIMO);
  // Menos de três pontos não delimita área: vira a caixa do traço, que é o
  // que o gestor vê acontecer ao riscar de leve (nunca uma forma vazia).
  if (pontos.length < LIMITES_FORMA.PONTOS_MINIMO) {
    const caixa = pontos.length ? caixaDePontos(pontos) : caixaEnvolvente(bruto);
    return caixa ? normalizarCaixa(TIPO_FORMA.RETANGULO, caixa) : null;
  }
  const original = caixaDePontos(pontos);
  // Pontos todos na mesma linha (risco reto de três toques) não cercam área:
  // esticar isso viraria um polígono achatado cuja caixa mente sobre o
  // desenho. O traço reto é um retângulo mínimo, como no risco curto.
  if (original.largura <= TOLERANCIA || original.altura <= TOLERANCIA) {
    return normalizarCaixa(TIPO_FORMA.RETANGULO, original);
  }
  const ajustada = encaixarNoMundo(crescerParaMinimo(original, LIMITES_FORMA.LADO_MINIMO));
  const finais = reprojetar(pontos, original, ajustada).map((p) => ponto(p.x, p.y));
  return Object.freeze({
    tipo: TIPO_FORMA.POLIGONO,
    x: arredondar(ajustada.x),
    y: arredondar(ajustada.y),
    largura: arredondar(ajustada.largura),
    altura: arredondar(ajustada.altura),
    pontos: Object.freeze(finais),
  });
}

/**
 * Forma pronta para gravar: dentro dos limites do backend, com lado mínimo,
 * caixa recalculada dos pontos e coordenadas arredondadas.
 *
 * `null` quando não há como salvar nada (nem caixa nem ponto legível) — o
 * chamador decide o que fazer; `tracado.js` nunca devolve null.
 */
export function normalizar(bruto) {
  if (!bruto || typeof bruto !== "object") return null;
  const tipo = TIPOS_FORMA.includes(bruto.tipo) ? bruto.tipo : TIPO_FORMA.RETANGULO;
  if (tipo === TIPO_FORMA.POLIGONO) return normalizarPoligono(bruto);
  return normalizarCaixa(tipo, bruto);
}

/** A forma é gravável (passou por `normalizar` sem virar null). */
export function ehFormaValida(forma) {
  return normalizar(forma) !== null;
}

// ------------------------------------------------------- posicionamento

/** Mesma forma deslocada em (dx, dy), já normalizada. */
export function mover(forma, dx, dy) {
  const atual = normalizar(forma);
  if (!atual || !finito(dx) || !finito(dy)) return atual;
  if (atual.tipo === TIPO_FORMA.POLIGONO) {
    return normalizar({ ...atual, pontos: atual.pontos.map((p) => ({ x: p.x + dx, y: p.y + dy })) });
  }
  return normalizar({ ...atual, x: atual.x + dx, y: atual.y + dy });
}

/**
 * Forma reprojetada para a caixa `{x?, y?, largura?, altura?}` — é o que a
 * alça de redimensionar produz. Campos ausentes ficam como estão.
 */
export function redimensionar(forma, caixa) {
  const atual = normalizar(forma);
  if (!atual) return null;
  const alvo = {
    x: finito(caixa?.x) ? caixa.x : atual.x,
    y: finito(caixa?.y) ? caixa.y : atual.y,
    largura: finito(caixa?.largura) ? caixa.largura : atual.largura,
    altura: finito(caixa?.altura) ? caixa.altura : atual.altura,
  };
  if (atual.tipo !== TIPO_FORMA.POLIGONO) return normalizar({ ...atual, ...alvo });
  const destino = encaixarNoMundo(crescerParaMinimo(alvo, LIMITES_FORMA.LADO_MINIMO));
  return normalizar({ ...atual, pontos: reprojetar(atual.pontos, caixaDePontos(atual.pontos), destino) });
}

/**
 * Lados de onde o gestor pode puxar uma forma para mudar o tamanho: as quatro
 * paredes e os quatro cantos. `n`/`s` mexem na altura, `e`/`w` na largura;
 * o canto mexe nas duas.
 */
export const LADOS_DE_PUXAR = Object.freeze(["n", "s", "e", "w", "ne", "nw", "se", "sw"]);

/**
 * Caixa nova ao puxar a forma pelo `lado`, com o dedo deslocado (dx, dy) em
 * coordenadas do MUNDO. O lado oposto fica parado: puxar a parede de cima
 * para baixo encolhe a sala por cima, sem arrastar o chão junto.
 *
 * Encolher além do lado mínimo não "vira" a sala do avesso: a parede puxada
 * para no mínimo, encostada na parede oposta.
 */
export function caixaAoPuxar(caixa, lado, dx, dy, minimo = LIMITES_FORMA.LADO_MINIMO) {
  if (!caixa || !LADOS_DE_PUXAR.includes(lado)) return caixa;
  const passoX = finito(dx) ? dx : 0;
  const passoY = finito(dy) ? dy : 0;
  let { x, y, largura, altura } = caixa;
  if (lado.includes("e")) largura = Math.max(minimo, caixa.largura + passoX);
  if (lado.includes("w")) {
    largura = Math.max(minimo, caixa.largura - passoX);
    x = caixa.x + caixa.largura - largura;
  }
  if (lado.includes("s")) altura = Math.max(minimo, caixa.altura + passoY);
  if (lado.includes("n")) {
    altura = Math.max(minimo, caixa.altura - passoY);
    y = caixa.y + caixa.altura - altura;
  }
  return { x, y, largura, altura };
}

/**
 * Forma deslocada (sem deformar) para caber dentro de `caixa` — o subsetor
 * arrastado para fora da sala volta para dentro em vez de sumir do mapa.
 * Quando não cabe nem deslocando, é reprojetada para a caixa.
 */
export function encaixarEm(forma, caixa) {
  const atual = normalizar(forma);
  const limite = caixaEnvolvente(caixa);
  if (!atual || !limite) return atual;
  if (atual.largura > limite.largura + TOLERANCIA || atual.altura > limite.altura + TOLERANCIA) {
    return redimensionar(atual, limite);
  }
  const dx = limitar(atual.x, limite.x, limite.x + limite.largura - atual.largura) - atual.x;
  const dy = limitar(atual.y, limite.y, limite.y + limite.altura - atual.altura) - atual.y;
  return dx || dy ? mover(atual, dx, dy) : atual;
}

// ------------------------------------------------------------- consultas

function dentroDaCaixa(caixa, p) {
  return (
    p.x >= caixa.x - TOLERANCIA &&
    p.x <= caixa.x + caixa.largura + TOLERANCIA &&
    p.y >= caixa.y - TOLERANCIA &&
    p.y <= caixa.y + caixa.altura + TOLERANCIA
  );
}

function dentroDaElipse(caixa, p) {
  const rx = caixa.largura / 2;
  const ry = caixa.altura / 2;
  if (rx <= 0 || ry <= 0) return false;
  const dx = (p.x - (caixa.x + rx)) / rx;
  const dy = (p.y - (caixa.y + ry)) / ry;
  return dx * dx + dy * dy <= 1 + TOLERANCIA;
}

/** Distância do ponto `p` ao segmento `a`–`b` (base da simplificação do traço). */
export function distanciaAoSegmento(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const comprimento = dx * dx + dy * dy;
  const t = comprimento > 0 ? limitar(((p.x - a.x) * dx + (p.y - a.y) * dy) / comprimento, 0, 1) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function noContorno(pontos, p) {
  return pontos.some((atual, i) => distanciaAoSegmento(p, pontos[(i + pontos.length - 1) % pontos.length], atual) <= TOLERANCIA);
}

function dentroDoPoligono(pontos, p) {
  let dentro = false;
  for (let i = 0, j = pontos.length - 1; i < pontos.length; j = i, i += 1) {
    const a = pontos[i];
    const b = pontos[j];
    // Um só dos dois extremos está acima do ponto: a aresta cruza a faixa
    // horizontal do ponto, então conta para o número de cruzamentos.
    const cruzaAFaixa = (a.y > p.y) !== (b.y > p.y);
    if (cruzaAFaixa && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) dentro = !dentro;
  }
  return dentro || noContorno(pontos, p);
}

/** O ponto `{x, y}` está dentro da forma (borda conta como dentro). */
export function pontoDentro(forma, p) {
  const caixa = caixaEnvolvente(forma);
  if (!caixa || !p || !finito(Number(p.x)) || !finito(Number(p.y))) return false;
  const alvo = { x: Number(p.x), y: Number(p.y) };
  if (!dentroDaCaixa(caixa, alvo)) return false;
  if (forma.tipo === TIPO_FORMA.ELIPSE) return dentroDaElipse(caixa, alvo);
  if (forma.tipo === TIPO_FORMA.POLIGONO) {
    const pontos = pontosValidos(forma.pontos);
    return pontos.length >= LIMITES_FORMA.PONTOS_MINIMO ? dentroDoPoligono(pontos, alvo) : true;
  }
  return true;
}

// Pontos que representam o contorno do filho no teste de continência.
function pontosDeProva(forma) {
  const caixa = caixaEnvolvente(forma);
  if (!caixa) return [];
  if (forma.tipo === TIPO_FORMA.POLIGONO) {
    const pontos = pontosValidos(forma.pontos);
    if (pontos.length >= LIMITES_FORMA.PONTOS_MINIMO) return pontos;
  }
  if (forma.tipo === TIPO_FORMA.ELIPSE) {
    const rx = caixa.largura / 2;
    const ry = caixa.altura / 2;
    return Array.from({ length: AMOSTRAS_ELIPSE }, (_, i) => {
      const angulo = (2 * Math.PI * i) / AMOSTRAS_ELIPSE;
      return { x: caixa.x + rx + rx * Math.cos(angulo), y: caixa.y + ry + ry * Math.sin(angulo) };
    });
  }
  return [
    { x: caixa.x, y: caixa.y },
    { x: caixa.x + caixa.largura, y: caixa.y },
    { x: caixa.x + caixa.largura, y: caixa.y + caixa.altura },
    { x: caixa.x, y: caixa.y + caixa.altura },
  ];
}

/**
 * O filho cabe dentro do pai — é o que valida a hierarquia do mapa
 * (posto dentro do subsetor, subsetor dentro do setor).
 *
 * Retângulo é exato. Elipse e polígono são testados por AMOSTRAGEM do
 * contorno do filho (a elipse-filha por 8 pontos): um contorno côncavo muito
 * recortado pode passar por um fio de cabelo. É o bastante para o gestor
 * saber que soltou o posto fora da sala, e é o mesmo teste do backend.
 */
export function contem(pai, filho) {
  const caixaPai = caixaEnvolvente(pai);
  const caixaFilho = caixaEnvolvente(filho);
  if (!caixaPai || !caixaFilho) return false;
  if (!dentroDaCaixa(caixaPai, { x: caixaFilho.x, y: caixaFilho.y })) return false;
  if (!dentroDaCaixa(caixaPai, { x: caixaFilho.x + caixaFilho.largura, y: caixaFilho.y + caixaFilho.altura })) {
    return false;
  }
  if (pai.tipo === TIPO_FORMA.RETANGULO || !TIPOS_FORMA.includes(pai.tipo)) return true;
  return pontosDeProva(filho).every((p) => pontoDentro(pai, p));
}

/** Duas formas são a mesma (para não gravar arraste que não mudou nada). */
export function mesmaForma(a, b) {
  const primeira = normalizar(a);
  const segunda = normalizar(b);
  if (!primeira || !segunda) return primeira === segunda;
  return JSON.stringify(primeira) === JSON.stringify(segunda);
}

// ----------------------------------------------------------------- SVG

const numero = (valor) => String(arredondar(valor));

/**
 * `d` de um `<path>` SVG, em coordenadas do mundo. A elipse é desenhada com
 * dois arcos (`A`) em vez de `<ellipse>` para que TODA forma do mapa seja um
 * `<path>` só — o mesmo elemento recebe borda, preenchimento e `clipPath`.
 */
export function paraPath(forma) {
  const caixa = caixaEnvolvente(forma);
  if (!caixa) return "";
  if (forma.tipo === TIPO_FORMA.ELIPSE) {
    const rx = caixa.largura / 2;
    const ry = caixa.altura / 2;
    const esquerda = `${numero(caixa.x)} ${numero(caixa.y + ry)}`;
    const direita = `${numero(caixa.x + caixa.largura)} ${numero(caixa.y + ry)}`;
    const arco = `${numero(rx)} ${numero(ry)} 0 1 1`;
    return `M ${esquerda} A ${arco} ${direita} A ${arco} ${esquerda} Z`;
  }
  const pontos = forma.tipo === TIPO_FORMA.POLIGONO ? pontosValidos(forma.pontos) : [];
  if (pontos.length >= LIMITES_FORMA.PONTOS_MINIMO) {
    const [primeiro, ...resto] = pontos;
    const linhas = resto.map((p) => `L ${numero(p.x)} ${numero(p.y)}`).join(" ");
    return `M ${numero(primeiro.x)} ${numero(primeiro.y)} ${linhas} Z`;
  }
  const { x, y, largura, altura } = caixa;
  return `M ${numero(x)} ${numero(y)} H ${numero(x + largura)} V ${numero(y + altura)} H ${numero(x)} Z`;
}
